import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { donationService, gurukulService, adminService } from '../../services';
import { API_BASE_URL } from '../../constants/config';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, toNepaliNumerals } from '../../utils/formatters';
import { fileToBase64 } from '../../utils/fileUtils';
import { DONATION_LIMITS, PAYMENT_METHODS } from '../../constants/config';
import QRCode from 'react-qr-code';
import axios from 'axios';

/** Always build on the client from token (+ optional ?d= payload); never use backend publicUrl. */
const buildVoucherUploadPublicUrl = (token, packed) => {
  if (!token || typeof window === 'undefined') return '';
  const q = packed ? `?d=${packed}` : '';
  return `${window.location.origin}/public/voucher-upload/${encodeURIComponent(token)}${q}`;
};

const PartyDonationModal = ({ isOpen, onClose, initialTargetType = 'PARTY', initialCandidateId = '' }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [amount, setAmount] = useState('');
  const [isAnonymous] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [targetType, setTargetType] = useState(initialTargetType);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState(initialCandidateId);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('VOUCHER');
  const [voucherFile, setVoucherFile] = useState(null);
  const [voucherUploadSession, setVoucherUploadSession] = useState(null); // { token, voucherUploadPacked, status, uploadedFileName, expiresAt }
  const [voucherUploadLoading, setVoucherUploadLoading] = useState(false);
  const [showVoucherUploadQr, setShowVoucherUploadQr] = useState(false);
  const [showGoalReachedModal, setShowGoalReachedModal] = useState(false);
  const [bankSettings, setBankSettings] = useState(null);
  const [loadingBank, setLoadingBank] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const modalBodyScrollRef = useRef(null);

  useEffect(() => {
    if (!error) return;
    modalBodyScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [error]);

  const encodeBase64Url = (obj) => {
    try {
      const json = JSON.stringify(obj);
      const b64 = btoa(unescape(encodeURIComponent(json)));
      return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    } catch {
      return null;
    }
  };

  const createOrOpenVoucherUploadQr = async () => {
    setError('');

    const donationAmount = parseFloat(amount);
    if (!donationAmount || donationAmount < DONATION_LIMITS.MIN || donationAmount > DONATION_LIMITS.MAX) {
      setError(t('form.validation.invalidAmount'));
      return;
    }

    if (targetType === 'CANDIDATE' && !selectedCandidateId) {
      setError(t('donate.modal.selectGurukul'));
      return;
    }

    // Reuse existing session if present and not expired (we don't know expiry reliably; keep it simple).
    if (voucherUploadSession?.token) {
      setShowVoucherUploadQr(true);
      return;
    }

    setVoucherUploadLoading(true);
    try {
      // Public endpoint expected (see backend prompt in my final message).
      const publicApi = axios.create({
        baseURL: API_BASE_URL,
        headers: { 'Content-Type': 'application/json' },
      });

      const resp = await publicApi.post('/public/voucher-upload-sessions', {
        purpose: 'DONATION_VOUCHER',
        amount: donationAmount,
        targetType,
        candidateId: targetType === 'CANDIDATE' ? selectedCandidateId : null,
      });

      const payload = resp?.data?.data || resp?.data || {};
      const token = payload.token || payload.sessionToken || payload.uploadToken;
      if (!token) {
        throw new Error('No upload token received');
      }

      const packed = encodeBase64Url({
        v: 1,
        token,
        amount: donationAmount,
        targetType,
        candidateId: targetType === 'CANDIDATE' ? selectedCandidateId : null,
      });

      setVoucherUploadSession({
        token,
        voucherUploadPacked: packed,
        status: payload.status || 'PENDING',
        uploadedFileName: payload.uploadedFileName || null,
        expiresAt: payload.expiresAt || null,
      });
      setShowVoucherUploadQr(true);
    } catch (err) {
      console.error('Failed creating voucher upload session:', err);
      setError(err?.response?.data?.message || 'Failed to create upload QR. Please try again.');
    } finally {
      setVoucherUploadLoading(false);
    }
  };

  const refreshVoucherUploadStatus = async () => {
    if (!voucherUploadSession?.token) return;
    try {
      const publicApi = axios.create({
        baseURL: API_BASE_URL,
        headers: { 'Content-Type': 'application/json' },
      });
      const resp = await publicApi.get(`/public/voucher-upload-sessions/${encodeURIComponent(voucherUploadSession.token)}`);
      const payload = resp?.data?.data || resp?.data || {};
      setVoucherUploadSession((prev) => ({
        ...(prev || {}),
        status: payload.status || prev?.status || 'PENDING',
        uploadedFileName: payload.uploadedFileName || prev?.uploadedFileName || null,
        expiresAt: payload.expiresAt || prev?.expiresAt || null,
      }));
    } catch (err) {
      console.error('Failed refreshing voucher upload session:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setTargetType(initialTargetType);
      setSelectedCandidateId(initialCandidateId);
      fetchCandidates();
      fetchBankSettings();
      setVoucherUploadSession(null);
      setShowVoucherUploadQr(false);
    }
  }, [isOpen, initialTargetType, initialCandidateId]);

  const normalizeUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('data:') || url.startsWith('http')) return url;
    const baseUrl = API_BASE_URL.split('/main/api/v1')[0];
    return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const copyText = async (key, text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(String(text));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const CopyIcon = ({ className = 'w-4 h-4' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8a2 2 0 012 2v10a2 2 0 01-2 2H8a2 2 0 01-2-2V9a2 2 0 012-2z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 17H5a2 2 0 01-2-2V5a2 2 0 012-2h10a2 2 0 012 2v1" />
    </svg>
  );

  const CheckIcon = ({ className = 'w-4 h-4' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );

  const fetchBankSettings = async () => {
    try {
      setLoadingBank(true);
      const response = await adminService.getBankSettings();
      setBankSettings(response?.data || response);
    } catch (err) {
      console.error('Error fetching bank settings:', err);
      // Default fallback
      setBankSettings({
        orgNameEn: 'Vaidik Anusandhan Guthi',
        orgNameNe: 'वैदिक अनुसन्धान गुठी',
        campaignName: 'दान सेवा अभियान',
        bankName: 'Nabil Bank Ltd.',
        accountNumber: '20601017500292',
        accountName: 'Vaidik Anusandhan Guthi',
        branch: 'Mitrapark Branch, Kathmandu',
        qrCodeUrl: '/fonepayqr.webp'
      });
    } finally {
      setLoadingBank(false);
    }
  };

  useEffect(() => {
    if (targetType === 'CANDIDATE' && selectedCandidateId && candidates.length > 0) {
      const candidate = candidates.find((c) => c.id === selectedCandidateId);
      if (candidate && candidate.goalAmount > 0 && Number(candidate.raisedAmount) >= Number(candidate.goalAmount)) {
        setShowGoalReachedModal(true);
      }
    }
  }, [selectedCandidateId, targetType, candidates]);

  const fetchCandidates = async () => {
    try {
      setLoadingCandidates(true);
      const response = await gurukulService.getAllGurukuls({ limit: 100 });
      setCandidates(response?.data?.items || []);
    } catch (err) {
      console.error('Error fetching candidates:', err);
    } finally {
      setLoadingCandidates(false);
    }
  };

  const quickAmounts = [1000, 5000, 10000, 50000, 100000];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!acceptedTerms) {
      setError(t('form.validation.termsRequired'));
      return;
    }

    // if (!isAuthenticated) {
    //   navigate('/login', { state: { from: '/', message: 'Please login to make a donation to the party' } });
    //   onClose();
    //   return;
    // }

    setError('');
    setSuccess('');

    const donationAmount = parseFloat(amount);

    // Validation
    if (!donationAmount || donationAmount < DONATION_LIMITS.MIN) {
      setError(`Minimum donation amount is ${formatCurrency(DONATION_LIMITS.MIN, lang)}`);
      return;
    }

    if (donationAmount > DONATION_LIMITS.MAX) {
      setError(`Maximum donation amount is ${formatCurrency(DONATION_LIMITS.MAX, lang)}`);
      return;
    }

    setProcessing(true);

    try {
      // Validation for candidate
      if (targetType === 'CANDIDATE' && !selectedCandidateId) {
        setError(t('donate.modal.selectGurukul'));
        setProcessing(false);
        return;
      }

      const paymentLabel = PAYMENT_METHODS.find((p) => p.id === selectedPaymentMethod)?.label || selectedPaymentMethod;

      // Initialize donation with selected payment method
      const donationData = {
        amount: donationAmount,
        targetType: targetType,
        candidateId: targetType === 'CANDIDATE' ? selectedCandidateId : null,
        isAnonymous: isAnonymous,
        paymentMethod: selectedPaymentMethod,
        returnUrl: `${window.location.origin}/payment/verify`,
      };

      if (selectedPaymentMethod === 'VOUCHER') {
        if (!voucherFile && !voucherUploadSession?.token) {
          setError(t('donations.uploadVoucher')); // Added this to plan
          setProcessing(false);
          return;
        }
        if (voucherFile) {
          try {
            const base64 = await fileToBase64(voucherFile);
            donationData.voucherImage = base64;
          } catch (uploadErr) {
            console.error('Voucher conversion failed:', uploadErr);
            setError('Failed to process voucher image. Please try again.');
            setProcessing(false);
            return;
          }
        } else if (voucherUploadSession?.token) {
          donationData.voucherUploadToken = voucherUploadSession.token;
        }
      }

      const data = await donationService.initializeDonation(donationData);

      console.log('Party donation initialization response:', data);

      // API wrapper: { status, statusCode, data: { success, message, donationId } }
      const payload = data?.data || data;

      if (selectedPaymentMethod === 'VOUCHER' && (payload?.success === true || payload?.status === 'success')) {
        const successMessage = payload.message || 'Voucher donation initialized. Please wait for verification.';

        // Clear form state for next open
        setAmount('');
        setVoucherFile(null);
        setVoucherUploadSession(null);
        setAcceptedTerms(false);
        setSelectedPaymentMethod('VOUCHER');
        setProcessing(false);
        setSuccess('');

        // Immediately close modal and show success on donations page
        onClose();
        navigate('/donations', { state: { message: successMessage } });
        return;
      }
    } catch (err) {
      console.error('Donation error:', err);
      setError(err.response?.data?.message || 'Failed to process donation. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const voucherUploadPublicUrl = useMemo(
    () =>
      buildVoucherUploadPublicUrl(
        voucherUploadSession?.token,
        voucherUploadSession?.voucherUploadPacked
      ),
    [voucherUploadSession?.token, voucherUploadSession?.voucherUploadPacked]
  );

  if (!isOpen) return null;

  const donationAmount = parseFloat(amount);
  const isAmountValid =
    !!donationAmount &&
    donationAmount >= DONATION_LIMITS.MIN &&
    donationAmount <= DONATION_LIMITS.MAX;

  const isTargetValid = targetType !== 'CANDIDATE' || !!selectedCandidateId;

  const sessionStatus = String(voucherUploadSession?.status || '').toUpperCase();
  const isOtherDeviceVoucherUploaded =
    !!voucherUploadSession?.token &&
    (sessionStatus === 'UPLOADED' || sessionStatus === 'FILE_UPLOADED' || sessionStatus === 'COMPLETED' || sessionStatus === 'SUCCESS');

  const isVoucherReady =
    selectedPaymentMethod !== 'VOUCHER' ||
    !!voucherFile ||
    isOtherDeviceVoucherUploaded;

  const canSubmitVoucher =
    !processing &&
    acceptedTerms &&
    isAmountValid &&
    isTargetValid &&
    isVoucherReady;

  const submitDisabledReason = !acceptedTerms
    ? t('form.validation.termsRequired')
    : !isAmountValid
      ? t('form.validation.invalidAmount')
      : !isTargetValid
        ? t('donate.modal.selectGurukul')
        : selectedPaymentMethod === 'VOUCHER' && !isVoucherReady
          ? 'Please upload the voucher (or finish other-device upload and refresh status).'
          : '';

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center p-4">
      <div className="relative w-full max-w-xl lg:max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5">
          <div className="flex justify-between items-center">
            <div>
              <div className="flex items-start gap-3">
                <div className="min-w-0">
                  <h3 className="text-2xl font-bold text-white">
                    {targetType === 'PARTY' ? t('donate.modal.titleParty') : t('donate.modal.titleCandidate')}
                  </h3>
                  <p className="text-primary-100 text-sm mt-1">{t('donate.modal.subtitle')}</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                onClose();
                setError('');
                setAmount('');
                setSuccess('');
                setAcceptedTerms(false);
                setSelectedPaymentMethod('VOUCHER');
              }}
              className="text-white hover:text-gray-200 transition p-2 hover:bg-white/10 rounded-lg"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div
          ref={modalBodyScrollRef}
          className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto"
        >
          {error && (
            <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-700 rounded-xl text-sm flex items-start gap-3 shadow-sm">
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border-2 border-green-200 text-green-700 rounded-xl text-sm flex items-center gap-3 shadow-sm">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">{success}</span>
            </div>
          )}

          {/* Support Selection */}
          <div className="mb-6">
            <label className="block text-base font-semibold text-gray-800 mb-3">
              {t('donate.modal.supportQuestion')}
            </label>
            <div className="flex gap-3">
              <label className={`flex-1 flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all shadow-sm ${targetType === 'PARTY'
                ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-md'
                : 'border-primary-200 hover:border-primary-400 hover:bg-primary-50'
                }`}>
                <input
                  type="radio"
                  name="targetType"
                  value="PARTY"
                  checked={targetType === 'PARTY'}
                  onChange={(e) => {
                    setTargetType(e.target.value);
                    setSelectedCandidateId('');
                  }}
                  className="sr-only"
                />
                <div className="text-center">
                  <p className="font-bold text-base">{t('donate.modal.gurukulFund')}</p>
                </div>
              </label>
              <label className={`flex-1 flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all shadow-sm ${targetType === 'CANDIDATE'
                ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-md'
                : 'border-primary-200 hover:border-primary-400 hover:bg-primary-50'
                }`}>
                <input
                  type="radio"
                  name="targetType"
                  value="CANDIDATE"
                  checked={targetType === 'CANDIDATE'}
                  onChange={(e) => setTargetType(e.target.value)}
                  className="sr-only"
                />
                <div className="text-center">
                  <p className="font-bold text-base">{t('donate.modal.aGurukul')}</p>
                </div>
              </label>
            </div>
          </div>

          {targetType === 'CANDIDATE' && (
            <div className="mb-6">
              <label className="block text-base font-semibold text-gray-800 mb-2">
                {t('donate.modal.selectGurukul')} <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedCandidateId}
                onChange={(e) => setSelectedCandidateId(e.target.value)}
                required={targetType === 'CANDIDATE'}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition text-base"
                disabled={loadingCandidates}
              >
                <option value="">{t('donate.modal.chooseGurukul')}</option>
                {candidates.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.name} {candidate.district ? `(${candidate.district})` : ''}
                  </option>
                ))}
              </select>
              {loadingCandidates && (
                <p className="mt-2 text-sm text-gray-500 flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('donate.modal.loading')}
                </p>
              )}
            </div>
          )}

          {targetType === 'PARTY' && (
            <div className="mb-6 p-5 bg-gradient-to-r from-primary-50 to-primary-100 border-2 border-primary-200 rounded-xl shadow-sm">
              <div className="flex items-center gap-4">
                <img
                  src="https://vag.org.np/wp-content/uploads/2022/02/LOGO-1.png"
                  alt="VAG Logo"
                  className="w-14 h-14 object-contain bg-white rounded-full p-1 shadow-md"
                />
                <div>
                  <p className="font-bold text-lg text-primary-900">
                    {lang === 'ne' ? (bankSettings?.orgNameNe || t('donate.modal.gurukulFundName')) : (bankSettings?.orgNameEn || t('donate.modal.gurukulFundName'))}
                  </p>
                  <p className="text-sm text-primary-700 mt-0.5">
                    {bankSettings?.campaignName || t('donate.modal.gurukulFundSubtitle')}
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Quick Amount Buttons */}
            <div>
              <label className="block text-base font-semibold text-gray-800 mb-3">
                {t('donate.modal.quickAmount')}
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {quickAmounts.map((quickAmount) => {
                  const displayAmount = quickAmount >= 1000
                    ? `${lang === 'ne' ? toNepaliNumerals(quickAmount / 1000) : quickAmount / 1000}K`
                    : (lang === 'ne' ? toNepaliNumerals(quickAmount) : quickAmount);
                  return (
                    <button
                      key={quickAmount}
                      type="button"
                      onClick={() => setAmount(quickAmount.toString())}
                      className={`px-2 py-3 sm:px-4 border-2 rounded-xl text-sm font-semibold transition-all shadow-sm ${amount === quickAmount.toString()
                        ? 'bg-primary-600 text-white border-primary-600 shadow-md scale-105'
                        : 'border-gray-300 text-gray-700 hover:bg-primary-50 hover:border-primary-300 hover:shadow'
                        }`}
                    >
                      {displayAmount}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom Amount */}
            <div>
              <label className="block text-base font-semibold text-gray-800 mb-2">
                {t('donate.modal.donationAmount')} ({lang === 'ne' ? 'रु' : 'NPR'}) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">{lang === 'ne' ? 'रु' : 'NPR'}</span>
                <input
                  type="number"
                  required
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition text-lg font-medium"
                  placeholder={t('donate.modal.enterAmount')}
                  min={DONATION_LIMITS.MIN}
                />
              </div>
              <p className="mt-2 text-sm text-gray-600">
                {t('donate.modal.minMax', { min: formatCurrency(DONATION_LIMITS.MIN, lang), max: formatCurrency(DONATION_LIMITS.MAX, lang) })}
              </p>
            </div>

            {/* Anonymous Option */}
            {/* <div className="flex items-center">
            <input
              type="checkbox"
              id="partyAnonymous"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="partyAnonymous" className="ml-2 block text-sm text-gray-900">
              Make this donation anonymous
            </label>
          </div> */}

            {/* Voucher Payment – always enabled */}
            {selectedPaymentMethod === 'VOUCHER' && (
              <div className="mb-6 p-4 border border-primary-200 rounded-xl bg-primary-50/50">
                <h4 className="font-bold text-gray-800 mb-2">{t('donate.modal.bankDetails')}</h4>
                <p className="text-sm text-gray-600 mb-4">Direct Bank Deposit / Transfer</p>

                <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-4">
                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm text-sm space-y-1.5">
                    <div className="flex justify-between border-b pb-1 mb-1">
                      <span className="text-gray-500">{t('donate.modal.bankName')}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">{bankSettings?.bankName || 'Nabil Bank Ltd.'}</span>
                        <button
                          type="button"
                          onClick={() => copyText('bankName', bankSettings?.bankName || 'Nabil Bank Ltd.')}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                          aria-label={t('actions.copy')}
                          title={t('actions.copy')}
                        >
                          {copiedKey === 'bankName' ? <CheckIcon className="w-4 h-4 text-green-600" /> : <CopyIcon className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between border-b pb-1 mb-1">
                      <span className="text-gray-500">{t('donate.modal.accountNo')}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-primary-700">{bankSettings?.accountNumber || '20601017500292'}</span>
                        <button
                          type="button"
                          onClick={() => copyText('accountNumber', bankSettings?.accountNumber || '20601017500292')}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-primary-200 text-primary-700 hover:bg-primary-50"
                          aria-label={t('actions.copy')}
                          title={t('actions.copy')}
                        >
                          {copiedKey === 'accountNumber' ? <CheckIcon className="w-4 h-4 text-green-700" /> : <CopyIcon className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between border-b pb-1 mb-1">
                      <span className="text-gray-500">{t('donate.modal.accountName')}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900 text-right">{bankSettings?.accountName || 'Vaidik Anusandhan Guthi'}</span>
                        <button
                          type="button"
                          onClick={() => copyText('accountName', bankSettings?.accountName || 'Vaidik Anusandhan Guthi')}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                          aria-label={t('actions.copy')}
                          title={t('actions.copy')}
                        >
                          {copiedKey === 'accountName' ? <CheckIcon className="w-4 h-4 text-green-600" /> : <CopyIcon className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('donate.modal.branch')}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">{bankSettings?.branch || 'Mitrapark Branch, Kathmandu'}</span>
                        <button
                          type="button"
                          onClick={() => copyText('branch', bankSettings?.branch || 'Mitrapark Branch, Kathmandu')}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                          aria-label={t('actions.copy')}
                          title={t('actions.copy')}
                        >
                          {copiedKey === 'branch' ? <CheckIcon className="w-4 h-4 text-green-600" /> : <CopyIcon className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          const lines = [
                            `${t('donate.modal.bankName')}: ${bankSettings?.bankName || 'Nabil Bank Ltd.'}`,
                            `${t('donate.modal.accountNo')}: ${bankSettings?.accountNumber || '20601017500292'}`,
                            `${t('donate.modal.accountName')}: ${bankSettings?.accountName || 'Vaidik Anusandhan Guthi'}`,
                            `${t('donate.modal.branch')}: ${bankSettings?.branch || 'Mitrapark Branch, Kathmandu'}`,
                          ];
                          copyText('bankAll', lines.join('\n'));
                        }}
                        className="w-full text-xs px-3 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 font-bold"
                      >
                        {copiedKey === 'bankAll' ? t('actions.copyAllSuccess') : t('actions.copyAll')}
                      </button>
                    </div>
                  </div>

                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm flex flex-col items-center justify-center w-full min-w-0 overflow-hidden">
                    <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide text-center px-1">{t('donate.modal.scanToPay')}</p>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center w-full max-w-full min-w-0">
                      <img
                        src={normalizeUrl(bankSettings?.qrCodeUrl) || "/fonepayqr.webp"}
                        alt="QR"
                        className="w-60 h-60 sm:w-72 sm:h-72 shrink-0 object-contain border border-gray-200 rounded p-1"
                      />
                      <img
                        src="/fonePayFull.webp"
                        alt="FonePay"
                        className="w-auto max-w-[9rem] sm:max-w-[10rem] h-auto max-h-14 sm:max-h-16 object-contain object-center min-w-0"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <label className="block text-sm font-semibold text-gray-800">
                      {t('donate.modal.uploadVoucher')} <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={createOrOpenVoucherUploadQr}
                      disabled={voucherUploadLoading}
                      className="inline-flex flex-shrink-0 items-center gap-2 px-3 py-2 rounded-lg border-2 border-primary-200 bg-white text-primary-700 hover:bg-primary-50 hover:border-primary-300 disabled:opacity-60 disabled:cursor-not-allowed"
                      aria-label="Upload from another device (QR)"
                      title="Upload from another device (QR)"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h3v3H7V7zm7 0h3v3h-3V7zM7 14h3v3H7v-3zm7 7h3v-3h-3v3zM14 14h3v3h-3v-3zM4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6z" />
                      </svg>
                      <span className="text-xs font-bold whitespace-nowrap">
                        QR upload
                      </span>
                    </button>
                  </div>

                  {voucherUploadSession?.token && !voucherFile && (
                    <div className="mb-3 p-3 rounded-lg border border-primary-200 bg-primary-50/60 text-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-primary-800">Other-device upload is enabled</p>
                          <p className="text-primary-700 mt-0.5">
                            Status: <span className="font-bold">{voucherUploadSession.status || 'PENDING'}</span>
                            {voucherUploadSession.uploadedFileName ? ` • File: ${voucherUploadSession.uploadedFileName}` : ''}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={refreshVoucherUploadStatus}
                          className="px-3 py-1.5 text-xs font-bold rounded-lg bg-white border border-primary-200 text-primary-700 hover:bg-primary-50"
                        >
                          Refresh
                        </button>
                      </div>
                      <p className="text-xs text-primary-700 mt-2">
                        You can still upload here on this device; selecting a file will override the QR flow.
                      </p>
                    </div>
                  )}

                  <label className="flex flex-col items-center px-4 py-4 bg-white text-blue rounded-lg shadow-sm tracking-wide uppercase border border-blue cursor-pointer hover:bg-blue-50 hover:text-blue-600 transition border-dashed border-gray-400">
                    <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M16.88 9.1A4 4 0 0 1 16 17H5a5 5 0 0 1-1-9.9V7a3 3 0 0 1 4.52-2.59A4.98 4.98 0 0 1 17 8c5 5 5 16.88 9.1zM10 14a1 1 0 0 0 1-1V9h2l-3-3-3 3h2v4a1 1 0 0 0 1 1z" />
                    </svg>
                    <span className="mt-1 text-sm leading-normal text-gray-500">{voucherFile ? voucherFile.name : t('donate.modal.selectFile')}</span>
                    <input
                      type='file'
                      className="hidden"
                      accept="image/*,application/pdf"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setVoucherFile(f);
                        if (f) {
                          setVoucherUploadSession(null);
                          setShowVoucherUploadQr(false);
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
            )}

            {/* Terms and Conditions - moved to bottom */}
            <div className="flex items-start p-4 bg-primary-50/50 rounded-xl border-2 border-primary-200">
              <div className="flex items-center h-5">
                <input
                  id="donationTerms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="donationTerms" className="text-gray-700 cursor-pointer">
                  {t('donate.modal.termsAgreement')}{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-primary-600 hover:text-primary-700 font-semibold underline"
                  >
                    {t('donate.modal.termsLink')}
                  </button>
                  <span className="text-red-500"> *</span>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  setError('');
                  setAmount('');
                  setSuccess('');
                  setAcceptedTerms(false);
                  setSelectedPaymentMethod('VOUCHER');
                }}
                className="flex-1 px-6 py-4 border-2 border-primary-300 text-primary-700 rounded-button hover:bg-primary-50 transition font-semibold text-base shadow-sm"
              >
                {t('donate.modal.cancel')}
              </button>
              <button
                type="submit"
                disabled={!canSubmitVoucher}
                title={!canSubmitVoucher && submitDisabledReason ? submitDisabledReason : undefined}
                className={`flex-1 px-6 py-4 rounded-button transition font-bold text-base shadow-lg ${!canSubmitVoucher
                  ? 'bg-gray-400 text-white cursor-not-allowed shadow'
                  : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 shadow-primary-500/50 hover:shadow-xl'
                  }`}
              >
                {processing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('donate.modal.processing')}
                  </span>
                ) : (
                  t('donate.modal.submitVoucher')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Goal Reached Modal */}
      {showGoalReachedModal && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black bg-opacity-70"></div>

            <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-2xl rounded-2xl border-t-8 border-primary-600">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 mb-4">
                  <svg className="h-10 w-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('donate.modal.goalReachedTitle')}</h3>
                <p className="text-gray-600 mb-6">
                  {t('donate.modal.goalReachedText')}
                </p>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => {
                      setTargetType('PARTY');
                      setSelectedCandidateId('');
                      setShowGoalReachedModal(false);
                    }}
                    className="w-full px-4 py-3 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-700 transition shadow-lg transform hover:-translate-y-0.5"
                  >
                    Donate to Gurukul Fund
                  </button>
                  <button
                    onClick={() => {
                      setShowGoalReachedModal(false);
                      // Optionally: setTargetType('PARTY'); setSelectedCandidateId(''); onClose(); if we want to force exit
                    }}
                    className="w-full px-4 py-3 bg-white border-2 border-primary-300 text-primary-700 font-semibold rounded-xl hover:bg-primary-50 transition"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
      }

      {/* Terms and Conditions Modal */}
      {
        showTermsModal && (
          <div className="fixed inset-0 z-[60] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
              <div
                className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                onClick={() => setShowTermsModal(false)}
              ></div>

              <div className="relative inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    {t('legal.terms.title')}
                  </h3>
                  <button
                    onClick={() => setShowTermsModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="max-h-96 overflow-y-auto pr-2 text-sm text-gray-700 space-y-4">
                  <h4 className="font-semibold text-gray-900">
                    {t('legal.terms.title')}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {t('legal.terms.meta.lastUpdated')}
                  </p>

                  <p className="text-gray-700 leading-relaxed">
                    {t('legal.terms.meta.intro')}
                  </p>

                  <div className="space-y-4">
                    {(t('legal.terms.sections', { returnObjects: true }) || []).map((section, idx) => (
                      <section key={idx} className="space-y-2">
                        <h5 className="font-semibold text-gray-800">{section.title}</h5>

                        {Array.isArray(section.paragraphs) && section.paragraphs.length > 0 && (
                          <div className="space-y-2">
                            {section.paragraphs.map((p, pIdx) => (
                              <p key={pIdx} className="text-gray-700 leading-relaxed">
                                {p}
                              </p>
                            ))}
                          </div>
                        )}

                        {Array.isArray(section.bullets) && section.bullets.length > 0 && (
                          <ul className="list-disc ml-5 space-y-1">
                            {section.bullets.map((b, bIdx) => (
                              <li key={bIdx}>{b}</li>
                            ))}
                          </ul>
                        )}
                      </section>
                    ))}
                  </div>

                  <div className="text-xs text-gray-500 mt-4 pt-4 border-t space-y-2">
                    <p className="font-semibold text-gray-700">{t('legal.terms.agreement.title')}</p>
                    <p>{t('legal.terms.agreement.text')}</p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => setShowTermsModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                  >
                    {t('common.close')}
                  </button>
                  <button
                    onClick={() => {
                      setAcceptedTerms(true);
                      setShowTermsModal(false);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition"
                  >
                    {t('actions.confirm')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Voucher Upload QR Modal */}
      {showVoucherUploadQr && voucherUploadSession?.token && voucherUploadPublicUrl && (
        <div className="fixed inset-0 z-[80] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-black bg-opacity-70"
              onClick={() => setShowVoucherUploadQr(false)}
            ></div>

            <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-2xl rounded-2xl">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Upload voucher from another device</h3>
                  <p className="text-sm text-gray-600 mt-1">Scan this QR on your other phone/laptop to upload the screenshot.</p>
                </div>
                <button
                  onClick={() => setShowVoucherUploadQr(false)}
                  className="text-gray-400 hover:text-gray-600 transition p-2 hover:bg-gray-50 rounded-lg"
                  aria-label={t('common.close')}
                  title={t('common.close')}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex flex-col items-center gap-4">
                <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                  <QRCode value={voucherUploadPublicUrl} size={220} />
                </div>

                <div className="w-full space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={voucherUploadPublicUrl}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-xs text-gray-700 bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={() => copyText('voucherUploadUrl', voucherUploadPublicUrl)}
                      className="px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs font-bold"
                    >
                      {copiedKey === 'voucherUploadUrl' ? 'Copied!' : t('actions.copy')}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Token: <span className="font-mono">{voucherUploadSession.token}</span>
                  </p>
                </div>

                <div className="w-full flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={refreshVoucherUploadStatus}
                    className="flex-1 px-4 py-2 rounded-lg bg-primary-600 text-white font-bold hover:bg-primary-700 transition"
                  >
                    Refresh status
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowVoucherUploadQr(false)}
                    className="flex-1 px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-800 font-semibold hover:bg-gray-50 transition"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div >
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default PartyDonationModal;
